/**
 * Mock-Schicht der SPA (nur Browser, nur Entwicklung und Vorab-Abnahme).
 *
 * WARUM: Die Endpunkte der Pakete 1–3 entstehen parallel. Damit die SPA vorher
 * vollstaendig klickbar ist, beantwortet diese Schicht /api-Aufrufe aus einem
 * deterministischen Datenbestand im Browser (daten.ts, handler.ts). Sie
 * liefert dieselben DTOs wie die echte API (contracts + api/typen.ts).
 *
 * MODI (Umgebungsvariable VITE_API_MOCK beim Start von Vite):
 *   VITE_API_MOCK=1   erzwungen: JEDER /api-Aufruf geht an den Mock, auch Login
 *                     und /system/status. Keine API noetig. So laufen die
 *                     Playwright-Szenarien in apps/web/e2e (npm run test:e2e -w @storeos/web).
 *   (nicht gesetzt)   Rueckfall: Aufruf geht an die echte API. Nur wenn sie mit
 *                     501, 404 „Unbekannter Endpunkt“ oder gar nicht (Netzfehler,
 *                     502–504) antwortet, springt der Mock fuer DIESEN Endpunkt
 *                     ein. Die Kopfzeile zeigt dann das Badge „SPA-MOCK“.
 *                     Fachliche 404 (z. B. Auftrag nicht gefunden) bleiben echt.
 *   VITE_API_MOCK=0   aus: nur die echte API. Default im Produktions-Build.
 *
 * NACH DEM MERGE: Sobald alle Endpunkte stehen, greift der Rueckfall nie mehr;
 * die SPA laeuft unveraendert gegen die echte API. Fuer den Betrieb wird mit
 * VITE_API_MOCK=0 gebaut (vite build setzt PROD; dort ist der Mock ohnehin aus,
 * solange VITE_API_MOCK nicht ausdruecklich 1 ist). Der Mock-Code wird nur bei
 * Bedarf nachgeladen (dynamic import) und liegt nicht im Hauptbundle.
 *
 * Zustand: sessionStorage (Tab-lokal), zuruecksetzbar ueber Einstellungen.
 */

export type MockModus = 'erzwungen' | 'rueckfall' | 'aus';

export function mockModus(): MockModus {
  const v = import.meta.env.VITE_API_MOCK as string | undefined;
  if (v === '1' || v === 'true') return 'erzwungen';
  if (v === '0' || v === 'false') return 'aus';
  return import.meta.env.PROD ? 'aus' : 'rueckfall';
}

/**
 * Detektor: Endpunkte (Methode + Pfad, IDs als :id), die im Rueckfall-Modus vom Mock
 * beantwortet wurden. Das Badge „SPA-MOCK“ zeigt die Liste; Ziel ist eine leere Liste.
 * Playwright liest sie ueber `window.__storeosSpaMock` bzw. das Badge-Attribut `data-endpunkte`.
 */
export const gemockt = new Set<string>();
if (typeof window !== 'undefined') (window as unknown as { __storeosSpaMock: Set<string> }).__storeosSpaMock = gemockt;
export const MOCK_EVENT = 'storeos:mock-benutzt';

type HandlerModul = typeof import('./handler');
let handlerModul: Promise<HandlerModul> | null = null;
const handler = () => (handlerModul ??= import('./handler'));

const verzoegerung = () => new Promise((r) => setTimeout(r, 40));

async function mockAntwort(methode: string, pfad: string, body: string | null): Promise<Response | null> {
  const h = await handler();
  let daten: Record<string, unknown> = {};
  try { daten = body ? (JSON.parse(body) as Record<string, unknown>) : {}; } catch { /* kein JSON */ }
  // Erst bearbeiten (Zustand sofort gespeichert, wie beim Server, der die Anfrage schon hat), dann verzoegert antworten:
  // sonst ginge z. B. ein Logout verloren, wenn die Seite direkt danach neu geladen wird.
  const r = h.mockAnfrage(methode, pfad, daten);
  await verzoegerung();
  if (!r) return null;
  return new Response(JSON.stringify(r.body), { status: r.status, headers: { 'Content-Type': 'application/json', 'X-StoreOS-Mock': '1' } });
}

function markiere(methode: string, pfad: string) {
  const schluessel = `${methode} ${pfad.replace(/\?.*$/, '').replace(/[0-9a-f-]{36}/g, ':id')}`;
  // Marker fuer Playwright (ueberlebt Navigationen, anders als das Set): e2e/hilfen.ts sammelt ihn.
  console.warn(`[SPA-MOCK] ${schluessel}`);
  if (!gemockt.has(schluessel)) {
    gemockt.add(schluessel);
    window.dispatchEvent(new CustomEvent(MOCK_EVENT));
  }
}

async function istUnbekannterEndpunkt(res: Response): Promise<boolean> {
  if (res.status === 501) return true;
  if (res.status >= 502 && res.status <= 504) return true;
  if (res.status !== 404) return false;
  const text = await res.clone().text().catch(() => '');
  return text.includes('Unbekannter Endpunkt') || text.includes('Cannot ');
}

let sessionGeprueft = 0;
/** Rueckfall: angemeldeten Benutzer der echten API in den Mock uebernehmen (hoechstens alle 30 s). */
async function sessionUebernehmen(original: typeof fetch) {
  if (Date.now() - sessionGeprueft < 30_000) return;
  sessionGeprueft = Date.now();
  const res = await original('/api/auth/me', { credentials: 'same-origin' }).catch(() => null);
  if (!res?.ok) return;
  const u = (await res.json().catch(() => null)) as { email: string; rolle: 'lager' | 'leitung' | 'admin' } | null;
  if (u) (await handler()).mockSessionSetzen(u);
}

/** Faengt fetch('/api/…') ab. Einmal beim Start aufrufen (main.tsx). */
export function installiereMock(): void {
  const modus = mockModus();
  if (modus === 'aus') return;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/api/')) return original(input, init);
    const pfad = url.pathname.slice(4) + url.search;
    const methode = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const body = typeof init?.body === 'string' ? init.body : null;

    if (modus === 'erzwungen') {
      return (await mockAntwort(methode, pfad, body)) ?? new Response(JSON.stringify({ fehler: `Mock kennt ${methode} ${pfad} nicht` }), { status: 404 });
    }
    const echt = await original(input, init).catch(() => null);
    if (echt && !(await istUnbekannterEndpunkt(echt))) return echt;
    await sessionUebernehmen(original);
    const m = await mockAntwort(methode, pfad, body);
    if (!m) return echt ?? Response.error();
    markiere(methode, pfad);
    return m;
  };
}

/** Blob-URL fuer Dateien (Label, Beleg-PDF, CSV) aus dem Mock; null = echte Datei verwenden. */
export async function mockDateiUrl(pfad: string): Promise<string | null> {
  if (mockModus() === 'rueckfall') {
    const kopf = await fetch(`/api${pfad}`, { method: 'HEAD' }).catch(() => null);
    if (kopf?.ok) return null;
  }
  const h = await handler();
  const blob = h.mockDatei(pfad);
  if (blob && mockModus() === 'rueckfall') markiere('GET', pfad);
  return blob ? URL.createObjectURL(blob) : null;
}

export async function mockZuruecksetzen(): Promise<void> {
  (await handler()).mockZuruecksetzen();
}
